// Reference-data family, part 2 (SEN-395 port wave): emission factors, energy-price writes, climate zones and
// regions, energy benchmarks, building-type ramp profiles, grid factors, the climate summary, ENTSO-E bidding zones,
// spot prices, grid carbon intensity and Google Places. Reads ./reference.ts's sibling records; reshape only.
//
// Sources (NomOS, all generic APIs; no bespoke endpoints except Places, which already exists):
//  - emission_factor / tariff / country / energy_source / climate_zone / climate_zone_region / energy_benchmark /
//    building_type_ramp_profile / bidding_zone records (objects/<type>/records/query|values, POST|PATCH|DELETE records).
//  - spot_price, grid_carbon_intensity, grid_renewable_pct, grid_generation_<fuel>: Aion series keyed by the
//    bidding_zone record, read with the query engine (POST /queries, time-bucket at hour grain, scalar avg/min/max).
//    Carbon intensity is a NomOS derivation (monad/energy_market/carbon.py, written with the generation mix);
//    nothing here computes it.
//  - Google Places: GET /places/config|autocomplete|{id}/details (Sentinel's shapes inside the Plenum envelope).
//
// Parity attributions (NomOS side is the intended one):
//  - Carbon: owner ruling 2026-10-12 -- NomOS per-(country, energy source) factors. Sentinel's per-year grid table
//    fields NomOS does not hold (scope2_location_based, scope2_market_based, renewable_pct, grid_region) read null.
//  - Spot prices / carbon are hourly (the Aion projection); Sentinel returns each 15-min ENTSO-E slot after the
//    2025-10-01 MTU switch (and so reports coverage above 100 %). Sentinel rounds to 1-2 dp; NomOS does not.
//  - Climate zones: NomOS holds 30 Koppen zones (Sentinel 26); the 4 extra carry no factor values (null, never 0).
//    total_hvac_factor is Sentinel's fixed weighting (0.6 x heating + 0.4 x cooling) of the record's own two factors.
import type {
  BiddingZone, BuildingTypeProfile, CarbonIntensityResponse, ClimateDataSummary, ClimateZone, EnergyBenchmark, EnergyPrice,
  EnergyPriceCreate, EnergySourceEmissionFactor, GridEmissionFactor, PlaceDetailsResponse, PlacesAutocompleteResponse, PriceResponse,
} from '@/types';
import {
  attributeIds, groupRecords, nomosFetch, queryRecords, refs, runQuery, scalar, toAxiosError, zulu,
  type NomosEnvelope, type NomosFilter, type NomosRow,
} from '../client';
import type { AnyFn, NomosRegistry } from '../registry';
import { countryCodes, countryRecordId } from './lookups';

type Row = Record<string, unknown>;
const str = (x: unknown) => (typeof x === 'string' && x !== '' ? x : null);
const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && x !== '' && Number.isFinite(Number(x)) ? Number(x) : null);
const v = (r: NomosRow, slug: string) => scalar(r.values[slug]);

/** Every record of a reference type (paged at 500). */
async function allRecords(type: string, fields: string[], filter?: NomosFilter): Promise<NomosRow[]> {
  const out: NomosRow[] = [];
  for (let offset = 0; ; offset += 500) {
    const page = await queryRecords(type, { fields, filter, limit: 500, cursor: offset });
    out.push(...page.rows);
    if (page.rows.length === 0 || out.length >= page.total) break;
  }
  return out;
}

// ---- energy sources (Sentinel ids are carried as sentinel_energy_source_id) ---------------------------------------
interface Source { recordId: string; id: string; code: string; name: string }
async function energySources(): Promise<Source[]> {
  return (await allRecords('energy_source', ['code', 'name', 'sentinel_name', 'sentinel_energy_source_id'])).map((r) => ({
    recordId: r.record_id, id: str(v(r, 'sentinel_energy_source_id')) ?? r.record_id, code: String(v(r, 'code') ?? ''),
    name: str(v(r, 'sentinel_name')) ?? str(v(r, 'name')) ?? r.kenning }));
}
const sourceBySentinelId = (all: Source[], id: string) => all.find((s) => s.id === id || s.recordId === id);

// ---------------------------------------------------------------------------------------------------------------
// Emission factors  GET /settings/emission-factors   (Sentinel ORDER BY energy source name, country, valid_from DESC)
// ---------------------------------------------------------------------------------------------------------------
const EF_FIELDS = ['country_ref', 'energy_source', 'emission_factor', 'valid_from', 'valid_to', 'source', 'site'];

async function getEmissionFactorsBySource(params?: { country_code?: string; energy_source_id?: string }): Promise<EnergySourceEmissionFactor[]> {
  const sources = await energySources();
  const clauses: NomosFilter[] = [];
  if (params?.country_code) {
    const cid = await countryRecordId(params.country_code);
    if (!cid) return [];
    clauses.push({ attribute: 'country_ref', op: 'eq', value: cid });
  }
  if (params?.energy_source_id) {
    const s = sourceBySentinelId(sources, params.energy_source_id);
    if (!s) return [];
    clauses.push({ attribute: 'energy_source', op: 'eq', value: s.recordId });
  }
  const [rows, countries] = await Promise.all([
    allRecords('emission_factor', EF_FIELDS, clauses.length === 1 ? clauses[0] : clauses.length ? { and: clauses } : undefined), countryCodes()]);
  const byRecord = new Map(sources.map((s) => [s.recordId, s]));
  const out: EnergySourceEmissionFactor[] = [];
  for (const r of rows) {
    if (refs(r.values.site).length) continue; // a site-specific factor is not a country reference row
    const c = refs(r.values.country_ref)[0]; const e = refs(r.values.energy_source)[0];
    const s = e ? byRecord.get(e.id) : undefined;
    if (!c || !s) continue;
    out.push({ id: r.record_id, country_code: countries.get(c.id) ?? '', energy_source_id: s.id, energy_source_code: s.code,
      energy_source_name: s.name, co2_kg_per_kwh: num(v(r, 'emission_factor')) as number, valid_from: str(v(r, 'valid_from')) as string,
      valid_to: str(v(r, 'valid_to')) ?? null, source: str(v(r, 'source')) ?? null } as unknown as EnergySourceEmissionFactor);
  }
  return out.sort((a, b) => a.energy_source_name.localeCompare(b.energy_source_name) || a.country_code.localeCompare(b.country_code)
    || (b.valid_from ?? '').localeCompare(a.valid_from ?? ''));
}

// ---------------------------------------------------------------------------------------------------------------
// Energy prices (tariff records)  POST/PUT/DELETE /settings/energy-prices
// A person's edit applies immediately; NomOS answering with a proposal (202) is surfaced as an error, never hidden.
// ---------------------------------------------------------------------------------------------------------------
const priceNotFound = (id: string) => toAxiosError(404, { detail: 'Energy price not found' }, `/settings/energy-prices/${id}`);

/** Sentinel tariff id (sentinel_tariff_id) or NomOS record id -> record id. */
async function tariffRecordId(id: string): Promise<string | undefined> {
  const bySentinel = await queryRecords('tariff', { filter: { attribute: 'sentinel_tariff_id', op: 'eq', value: id }, limit: 1 });
  if (bySentinel.rows[0]) return bySentinel.rows[0].record_id;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return undefined;
  try { await nomosFetch('GET', `/objects/tariff/records/${encodeURIComponent(id)}`); return id; } catch (e) {
    if ((e as { response?: { status?: number } }).response?.status === 404) return undefined;
    throw e;
  }
}

type RecordData = { record_id: string; created_at: string; values: Record<string, unknown> };

function refused(res: NomosEnvelope<unknown>, url: string): never {
  throw toAxiosError(409, { detail: 'NomOS queued this edit for review instead of applying it; a person\'s edit must apply immediately',
    error: { code: 'write_proposed', message: JSON.stringify(res.data) } }, url);
}

async function tariffWrite(method: 'POST' | 'PATCH', path: string, values: Row): Promise<RecordData> {
  const res = await nomosFetch<NomosEnvelope<RecordData & { proposed_change_id?: string; status?: string }>>(method, path, { values });
  if (!res.data || !('record_id' in res.data)) refused(res as NomosEnvelope<unknown>, path);
  return res.data;
}

/** A tariff record's wire values (attribute_id keyed) -> Sentinel EnergyPrice. */
async function priceFromRecord(d: RecordData, updatedAt: string): Promise<EnergyPrice> {
  const ids = await attributeIds('tariff');
  const val = (slug: string) => d.values[ids[slug]];
  const c = refs(val('country_ref'))[0]; const e = refs(val('energy_source'))[0];
  const [countries, sources] = await Promise.all([countryCodes(), energySources()]);
  const s = e ? sources.find((x) => x.recordId === e.id) : undefined;
  return { id: str(scalar(val('sentinel_tariff_id'))) ?? d.record_id, country_code: c ? countries.get(c.id) ?? null : null,
    energy_source_id: s?.id ?? e?.id ?? null, energy_source_name: s?.name ?? e?.name ?? null, price_per_kwh: num(scalar(val('price_per_kwh'))),
    currency: str(scalar(val('currency'))), valid_from: str(scalar(val('valid_from'))), valid_to: str(scalar(val('valid_to'))),
    source: str(scalar(val('source'))), created_at: zulu(d.created_at), updated_at: zulu(updatedAt) } as unknown as EnergyPrice;
}

/** The record's last write (its timeline's newest event) = Sentinel's updated_at. */
async function lastWrite(recordId: string, fallback: string): Promise<string> {
  const t = await nomosFetch<NomosEnvelope<{ events: Array<{ t: string }> }>>('GET', `/records/${encodeURIComponent(recordId)}/timeline`, undefined, { query: { limit: 1 } });
  return t.data?.events?.[0]?.t ?? fallback;
}

/** Sentinel field -> tariff values (country code / energy source id resolved to records; name = "<CC> · <source code>"). */
async function tariffValues(data: Partial<EnergyPriceCreate>, current?: { cc: string | null; code: string | null }): Promise<Row> {
  const out: Row = {};
  let cc = current?.cc ?? null; let code = current?.code ?? null;
  if (data.country_code !== undefined) {
    const id = data.country_code ? await countryRecordId(data.country_code) : undefined;
    if (!id) throw toAxiosError(422, { detail: `Unknown country_code '${data.country_code}'` }, '/settings/energy-prices');
    out.country_ref = id; cc = data.country_code.toUpperCase();
  }
  if (data.energy_source_id !== undefined) {
    const s = sourceBySentinelId(await energySources(), data.energy_source_id);
    if (!s) throw toAxiosError(422, { detail: `Unknown energy_source_id '${data.energy_source_id}'` }, '/settings/energy-prices');
    out.energy_source = s.recordId; code = s.code;
  }
  if (data.price_per_kwh !== undefined) out.price_per_kwh = data.price_per_kwh;
  if (data.currency !== undefined) out.currency = data.currency ? data.currency.toUpperCase() : data.currency;
  if (data.valid_from !== undefined) out.valid_from = data.valid_from;
  if (data.valid_to !== undefined) out.valid_to = data.valid_to ?? null;
  if (data.source !== undefined) out.source = data.source ?? null;
  if ((out.country_ref || out.energy_source) && cc && code) out.name = `${cc} · ${code}`;
  return out;
}

async function createEnergyPrice(data: EnergyPriceCreate): Promise<EnergyPrice> {
  const values = await tariffValues({ currency: 'EUR', ...data });
  const d = await tariffWrite('POST', '/objects/tariff/records', values);
  return priceFromRecord(d, d.created_at);
}

async function updateEnergyPrice(id: string, data: Partial<EnergyPriceCreate>): Promise<EnergyPrice> {
  const fields = Object.fromEntries(Object.entries(data).filter(([, x]) => x !== undefined)) as Partial<EnergyPriceCreate>;
  if (!Object.keys(fields).length) throw toAxiosError(400, { detail: 'No fields to update' }, `/settings/energy-prices/${id}`);
  const rid = await tariffRecordId(id);
  if (!rid) throw priceNotFound(id);
  let current: { cc: string | null; code: string | null } | undefined;
  if ((fields.country_code === undefined) !== (fields.energy_source_id === undefined)) {
    // one half of the record's name changes: read the other half from the record
    const [rec, ids, countries, sources] = await Promise.all([
      nomosFetch<NomosEnvelope<RecordData>>('GET', `/objects/tariff/records/${encodeURIComponent(rid)}`), attributeIds('tariff'), countryCodes(), energySources()]);
    const c = refs(rec.data.values[ids.country_ref])[0]; const e = refs(rec.data.values[ids.energy_source])[0];
    current = { cc: c ? countries.get(c.id) ?? null : null, code: e ? sources.find((x) => x.recordId === e.id)?.code ?? null : null };
  }
  const values = await tariffValues(fields, current);
  const d = await tariffWrite('PATCH', `/objects/tariff/records/${encodeURIComponent(rid)}`, values);
  return priceFromRecord(d, await lastWrite(rid, d.created_at));
}

async function deleteEnergyPrice(id: string): Promise<void> {
  const rid = await tariffRecordId(id);
  if (!rid) throw priceNotFound(id);
  const res = await nomosFetch<NomosEnvelope<{ archived?: boolean }>>('DELETE', `/objects/tariff/records/${encodeURIComponent(rid)}`);
  if (!res.data?.archived) refused(res as NomosEnvelope<unknown>, `/objects/tariff/records/${rid}`);
}

// ---------------------------------------------------------------------------------------------------------------
// Climate  GET /climate/zones, /climate/lookup, /climate/benchmarks, /climate/emission-factors, /climate/summary
// ---------------------------------------------------------------------------------------------------------------
const ZONE_FIELDS = ['code', 'name', 'description', 'avg_temp_summer_c', 'avg_temp_winter_c', 'heating_degree_days',
  'cooling_degree_days', 'heating_factor', 'cooling_factor', 'data_source'];

function zoneFromRow(r: NomosRow): ClimateZone {
  const hf = num(v(r, 'heating_factor')); const cf = num(v(r, 'cooling_factor'));
  return { code: String(v(r, 'code') ?? ''), name: str(v(r, 'name')) ?? r.kenning, description: str(v(r, 'description')),
    avg_temp_summer_c: num(v(r, 'avg_temp_summer_c')), avg_temp_winter_c: num(v(r, 'avg_temp_winter_c')),
    heating_degree_days: num(v(r, 'heating_degree_days')), cooling_degree_days: num(v(r, 'cooling_degree_days')),
    heating_factor: hf, cooling_factor: cf, total_hvac_factor: hf !== null && cf !== null ? hf * 0.6 + cf * 0.4 : null,
    data_source: str(v(r, 'data_source')) } as unknown as ClimateZone;
}

async function getClimateZones(): Promise<ClimateZone[]> {
  return (await allRecords('climate_zone', ZONE_FIELDS)).map(zoneFromRow).sort((a, b) => a.code.localeCompare(b.code));
}

/** Sentinel get_climate_zone_for_location: the highest-priority region box holding the point; none -> Cfb (Sentinel's default). */
async function lookupClimateZone(latitude: number, longitude: number) {
  if (!(latitude >= -90 && latitude <= 90) || !(longitude >= -180 && longitude <= 180)) {
    throw toAxiosError(422, { detail: 'latitude must be within -90..90 and longitude within -180..180' }, '/climate/lookup');
  }
  const inside: NomosFilter = { and: [
    { attribute: 'lat_min', op: 'lte', value: latitude }, { attribute: 'lat_max', op: 'gte', value: latitude },
    { attribute: 'lon_min', op: 'lte', value: longitude }, { attribute: 'lon_max', op: 'gte', value: longitude }] };
  const regions = await queryRecords('climate_zone_region', { filter: inside, fields: ['code', 'priority', 'climate_zone'], limit: 100 });
  const best = regions.rows.sort((a, b) => (num(v(b, 'priority')) ?? 0) - (num(v(a, 'priority')) ?? 0)
    || String(v(a, 'code')).localeCompare(String(v(b, 'code'))))[0];
  const zoneId = best ? refs(best.values.climate_zone)[0]?.id : undefined;
  const zones = await allRecords('climate_zone', ZONE_FIELDS);
  const zone = { rows: zones.filter((r) => (zoneId ? r.record_id === zoneId : v(r, 'code') === 'Cfb')) };
  const row = zone.rows[0];
  return row
    ? { latitude, longitude, climate_zone: zoneFromRow(row), message: null }
    : { latitude, longitude, climate_zone: null, message: 'Could not determine climate zone for this location' };
}

const BENCH_FIELDS = ['climate_zone', 'building_type', 'kwh_per_sqm_total', 'kwh_per_sqm_heating', 'kwh_per_sqm_cooling',
  'kwh_per_sqm_lighting', 'kwh_per_sqm_equipment', 'data_source', 'data_year', 'region_applicability'];

async function zoneCodes(): Promise<Map<string, string>> {
  return new Map((await allRecords('climate_zone', ['code'])).map((r) => [r.record_id, String(v(r, 'code') ?? '')]));
}

async function getEnergyBenchmarks(): Promise<EnergyBenchmark[]> {
  const [rows, zones] = await Promise.all([allRecords('energy_benchmark', BENCH_FIELDS), zoneCodes()]);
  return rows.map((r) => ({ climate_zone_code: zones.get(refs(r.values.climate_zone)[0]?.id ?? '') ?? '',
    building_type: String(v(r, 'building_type') ?? ''), kwh_per_sqm_total: num(v(r, 'kwh_per_sqm_total')),
    kwh_per_sqm_heating: num(v(r, 'kwh_per_sqm_heating')), kwh_per_sqm_cooling: num(v(r, 'kwh_per_sqm_cooling')),
    kwh_per_sqm_lighting: num(v(r, 'kwh_per_sqm_lighting')), kwh_per_sqm_equipment: num(v(r, 'kwh_per_sqm_equipment')),
    data_source: String(v(r, 'data_source') ?? ''), data_year: num(v(r, 'data_year')), region_applicability: str(v(r, 'region_applicability')) }))
    .sort((a, b) => a.climate_zone_code.localeCompare(b.climate_zone_code) || a.building_type.localeCompare(b.building_type)) as EnergyBenchmark[];
}

/** The electricity energy_source record (grid factors are its country factors). */
async function electricity(): Promise<Source> {
  const s = (await energySources()).find((x) => x.code === 'electricity');
  if (!s) throw toAxiosError(500, { detail: 'NomOS has no electricity energy source' }, '/climate/emission-factors');
  return s;
}

async function gridFactorRows() {
  const el = await electricity();
  const rows = await allRecords('emission_factor', EF_FIELDS, { attribute: 'energy_source', op: 'eq', value: el.recordId });
  return rows.filter((r) => !refs(r.values.site).length && refs(r.values.country_ref).length);
}

/** Owner ruling: grid factors ARE NomOS's per-(country, electricity) emission factors; year = valid_from's year. */
async function getGridEmissionFactors(): Promise<GridEmissionFactor[]> {
  const [rows, countries] = await Promise.all([gridFactorRows(), countryCodes()]);
  return rows.map((r) => {
    const c = refs(r.values.country_ref)[0]; const from = str(v(r, 'valid_from'));
    return { country_code: countries.get(c.id) ?? '', country_name: c.name, grid_region: null, year: from ? Number(from.slice(0, 4)) : null,
      co2_kg_per_kwh: num(v(r, 'emission_factor')), scope2_location_based: null, scope2_market_based: null, renewable_pct: null,
      data_source: str(v(r, 'source')) };
  }).sort((a, b) => a.country_code.localeCompare(b.country_code) || (b.year ?? -1) - (a.year ?? -1)) as unknown as GridEmissionFactor[];
}

/** Counts are NomOS's (pagination totals and record groups); last_updated = when this tenant's climate reference was seeded. */
async function getClimateDataSummary(): Promise<ClimateDataSummary> {
  const total = async (type: string, filter?: NomosFilter) =>
    (await nomosFetch<NomosEnvelope<unknown[]>>('POST', `/objects/${type}/records/query`, { limit: 1, ...(filter ? { filter } : {}) })).meta?.pagination?.total ?? 0;
  const [zones, regions, benchmarks, factors, zoneSources, benchSources, firstZone] = await Promise.all([
    total('climate_zone'), total('climate_zone_region'), total('energy_benchmark'), gridFactorRows(),
    groupRecords('climate_zone', { groupBy: ['data_source'] }), groupRecords('energy_benchmark', { groupBy: ['data_source'] }),
    queryRecords('climate_zone', { limit: 1 }),
  ]);
  const years = factors.map((r) => str(v(r, 'valid_from'))).filter((x): x is string => !!x).map((x) => Number(x.slice(0, 4)));
  const sources = new Set<string>();
  for (const g of [...zoneSources, ...benchSources]) if (typeof g.key[0] === 'string' && g.key[0]) sources.add(g.key[0]);
  for (const r of factors) { const s = str(v(r, 'source')); if (s) sources.add(s); }
  let lastUpdated: string | null = null;
  if (firstZone.rows[0]) {
    lastUpdated = (await nomosFetch<NomosEnvelope<{ created_at: string }>>('GET', `/objects/climate_zone/records/${firstZone.rows[0].record_id}`)).data?.created_at ?? null;
  }
  return { climate_zones_count: zones, climate_regions_count: regions, energy_benchmarks_count: benchmarks,
    emission_factors_count: factors.length, countries_with_emissions: new Set(factors.map((r) => refs(r.values.country_ref)[0].id)).size,
    latest_emission_year: years.length ? years.reduce((a, b) => (b > a ? b : a)) : null, data_sources: [...sources].sort(),
    last_updated: lastUpdated } as ClimateDataSummary;
}

// ---------------------------------------------------------------------------------------------------------------
// Building-type ramp profiles  GET/PUT /settings/building-type-profiles
// ---------------------------------------------------------------------------------------------------------------
const RAMP_FIELDS = ['code', 'ramp_up_hours', 'ramp_down_hours', 'default_ramp_up_hours', 'default_ramp_down_hours'];

async function siteCounts(): Promise<Map<string, number>> {
  const groups = await groupRecords('site', { groupBy: ['building_type'], filter: { attribute: 'is_active', op: 'eq', value: true } });
  const m = new Map<string, number>();
  for (const g of groups) { const k = typeof g.key[0] === 'string' && g.key[0] ? g.key[0] : 'other'; m.set(k, (m.get(k) ?? 0) + g.count); }
  return m;
}

function profile(r: NomosRow, counts: Map<string, number>): BuildingTypeProfile {
  const up = num(v(r, 'ramp_up_hours')) as number; const down = num(v(r, 'ramp_down_hours')) as number;
  const dUp = num(v(r, 'default_ramp_up_hours')); const dDown = num(v(r, 'default_ramp_down_hours'));
  const code = String(v(r, 'code') ?? '');
  return { building_type: code, ramp_up_hours: up, ramp_down_hours: down, default_ramp_up_hours: dUp as number,
    default_ramp_down_hours: dDown as number, is_override: dUp !== null && dDown !== null ? up !== dUp || down !== dDown : false,
    building_count: counts.get(code) ?? 0 };
}

async function getBuildingTypeProfiles(): Promise<BuildingTypeProfile[]> {
  const [rows, counts] = await Promise.all([allRecords('building_type_ramp_profile', RAMP_FIELDS), siteCounts()]);
  return rows.map((r) => profile(r, counts));
}

async function updateBuildingTypeProfile(buildingType: string, data: { ramp_up_hours: number; ramp_down_hours: number }): Promise<BuildingTypeProfile> {
  const url = `/settings/building-type-profiles/${buildingType}`;
  const page = await queryRecords('building_type_ramp_profile', { filter: { attribute: 'code', op: 'eq', value: buildingType }, limit: 1 });
  const rid = page.rows[0]?.record_id;
  if (!rid) throw toAxiosError(400, { detail: `Unknown building type: ${buildingType}` }, url);
  const res = await nomosFetch<NomosEnvelope<RecordData>>('PATCH', `/objects/building_type_ramp_profile/records/${encodeURIComponent(rid)}`,
    { values: { ramp_up_hours: data.ramp_up_hours, ramp_down_hours: data.ramp_down_hours } });
  if (!res.data || !('record_id' in res.data)) refused(res as NomosEnvelope<unknown>, url);
  const [after, counts] = await Promise.all([
    queryRecords('building_type_ramp_profile', { filter: { attribute: 'code', op: 'eq', value: buildingType }, fields: RAMP_FIELDS, limit: 1 }),
    siteCounts()]);
  return profile(after.rows[0], counts);
}

// ---------------------------------------------------------------------------------------------------------------
// ENTSO-E  GET /bidding-zones, /bidding-zones/{code}/prices, /bidding-zones/{code}/carbon
// ---------------------------------------------------------------------------------------------------------------
async function zoneRows(): Promise<NomosRow[]> { return allRecords('bidding_zone', ['code', 'name', 'eic_code', 'country_code']); }

/** The zone's timezone is its country's (NomOS keeps one timezone, on the Country record). */
async function getBiddingZones(): Promise<BiddingZone[]> {
  const [zones, countries] = await Promise.all([zoneRows(), allRecords('country', ['code', 'timezone'])]);
  const tz = new Map(countries.map((c) => [String(v(c, 'code') ?? '').toUpperCase(), str(v(c, 'timezone'))]));
  return zones.map((r) => {
    const cc = String(v(r, 'country_code') ?? '');
    return { code: String(v(r, 'code') ?? ''), name: str(v(r, 'name')) ?? r.kenning, country_code: cc, timezone: tz.get(cc.toUpperCase()) ?? null,
      boundary_geojson: null, eic_code: str(v(r, 'eic_code')), is_active: true };
  }).sort((a, b) => a.code.localeCompare(b.code)) as unknown as BiddingZone[];
}

const DAY = 86_400_000;
/** Sentinel's checks: the zone exists (404), end >= start, at most 365 days (400). Returns the UTC window [start, end+1d). */
async function marketWindow(code: string, startDate: string, endDate: string, what: string) {
  const zone = code.toUpperCase();
  const url = `/bidding-zones/${code}/${what}`;
  const found = await queryRecords('bidding_zone', { filter: { attribute: 'code', op: 'eq', value: zone }, limit: 1 });
  if (!found.rows[0]) throw toAxiosError(404, { detail: `Bidding zone '${code}' not found` }, url);
  const s = Date.parse(`${startDate}T00:00:00Z`); const e = Date.parse(`${endDate}T00:00:00Z`);
  if (Number.isNaN(s) || Number.isNaN(e)) throw toAxiosError(422, { detail: 'start_date and end_date must be YYYY-MM-DD' }, url);
  if (e < s) throw toAxiosError(400, { detail: 'end_date must be >= start_date' }, url);
  if ((e - s) / DAY > 365) throw toAxiosError(400, { detail: 'Maximum date range is 365 days' }, url);
  return { zone, filter: { attribute: 'code', op: 'eq', value: zone } as NomosFilter,
    period_start: new Date(s).toISOString(), period_end: new Date(e + DAY).toISOString(), hours: (e + DAY - s) / 3_600_000 };
}

async function hourly(w: { filter: NomosFilter; period_start: string; period_end: string }, measures: string[], agg: string): Promise<Row[]> {
  const rows: Row[] = [];
  let cursor: string | undefined;
  for (let guard = 0; guard < 100; guard++) {
    const res = await runQuery<{ rows: Row[]; meta: Row }>({ op: 'reduce',
      scope: { entity_def: 'bidding_zone', filter: w.filter, period_start: w.period_start, period_end: w.period_end },
      reducer: { kind: 'time-bucket', agg, granularity: 'hour', tz: 'UTC' },
      measures: measures.map((m) => ({ as: m, metric: m })), page: { size: 10_000, ...(cursor ? { cursor } : {}) } });
    rows.push(...(res.data?.rows ?? []));
    cursor = ((res.data?.meta?.page as Row | undefined)?.cursor as string | null | undefined) ?? undefined;
    if (!cursor) break;
  }
  return rows.sort((a, b) => String(a.bucket_start).localeCompare(String(b.bucket_start)));
}

/** avg / min / max of a series over the window, each answered by the engine. */
async function stats(w: { filter: NomosFilter; period_start: string; period_end: string }, quantity: string) {
  const one = async (agg: string) => num((await runQuery<{ rows: Row[] }>({ op: 'reduce',
    scope: { entity_def: 'bidding_zone', filter: w.filter, period_start: w.period_start, period_end: w.period_end, quantity },
    reducer: { kind: 'scalar', agg } })).data?.rows?.[0]?.value);
  const [avg, min, max] = await Promise.all([one('avg'), one('min'), one('max')]);
  return { avg, min, max };
}

const utcStamp = (x: unknown) => zulu(new Date(String(x)).toISOString().replace('.000Z', 'Z'));

async function getSpotPrices(code: string, startDate: string, endDate: string): Promise<PriceResponse> {
  const w = await marketWindow(code, startDate, endDate, 'prices');
  const rows = (await hourly(w, ['spot_price'], 'avg')).filter((r) => num(r.spot_price) !== null);
  const prices = rows.map((r) => {
    const da = num(r.spot_price) as number;
    return { timestamp_utc: utcStamp(r.bucket_start), day_ahead_eur_mwh: da, actual_eur_mwh: null, day_ahead_eur_kwh: da / 1000, actual_eur_kwh: null };
  });
  let summary: Row | null = null;
  if (prices.length) {
    const s = await stats(w, 'spot_price');
    summary = { day_ahead: { avg_eur_mwh: s.avg, min_eur_mwh: s.min, max_eur_mwh: s.max } }; // Sentinel's wire shape (no actual prices in NomOS)
  }
  return { bidding_zone: w.zone, energy_type: 'electricity', start_date: startDate, end_date: endDate, hours_in_range: w.hours,
    hours_with_data: prices.length, coverage_pct: w.hours > 0 ? (prices.length / w.hours) * 100 : 0, prices, summary } as unknown as PriceResponse;
}

const FUELS = ['biomass', 'fossil_brown_coal', 'fossil_coal_gas', 'fossil_gas', 'fossil_hard_coal', 'fossil_oil', 'fossil_oil_shale',
  'fossil_peat', 'geothermal', 'hydro_pumped', 'hydro_run_of_river', 'hydro_reservoir', 'marine', 'nuclear', 'other_renewable', 'solar',
  'waste', 'wind_offshore', 'wind_onshore', 'other'] as const;

async function getCarbonIntensity(code: string, startDate: string, endDate: string): Promise<CarbonIntensityResponse> {
  const w = await marketWindow(code, startDate, endDate, 'carbon');
  // 20 fuels > the engine's 16-measure cap per query (MAX_MEASURES): ask in two halves, merge by hour.
  const fuels = FUELS.map((f) => `grid_generation_${f}`);
  const [carbon, genA, genB] = await Promise.all([
    hourly(w, ['grid_carbon_intensity', 'grid_renewable_pct'], 'avg'),
    hourly(w, fuels.slice(0, 10), 'sum'),
    hourly(w, fuels.slice(10), 'sum'),
  ]);
  const mixAt = new Map<string, Row>();
  for (const r of [...genA, ...genB]) { const ts = utcStamp(r.bucket_start); mixAt.set(ts, { ...mixAt.get(ts), ...r }); }
  const data = carbon.filter((r) => num(r.grid_carbon_intensity) !== null).map((r) => {
    const ts = utcStamp(r.bucket_start);
    const g = mixAt.get(ts) ?? {};
    const generation_mix: Record<string, number> = {};
    for (const f of FUELS) { const x = num(g[`grid_generation_${f}`]); if (x !== null) generation_mix[f] = x; }
    return { timestamp_utc: ts, carbon_g_per_kwh: num(r.grid_carbon_intensity) as number, renewable_pct: num(r.grid_renewable_pct) as number, generation_mix };
  });
  let summary: Row | null = null;
  if (data.length) {
    const s = await stats(w, 'grid_carbon_intensity');
    summary = { avg_g_per_kwh: s.avg, min_g_per_kwh: s.min, max_g_per_kwh: s.max };
  }
  return { zone_code: w.zone, start_date: startDate, end_date: endDate, hours_with_data: data.length, data, summary } as unknown as CarbonIntensityResponse;
}

// ---------------------------------------------------------------------------------------------------------------
// Google Places  GET /places/config, /places/autocomplete, /places/{id}/details
// ---------------------------------------------------------------------------------------------------------------
async function checkPlacesConfig(): Promise<{ configured: boolean }> {
  const r = await nomosFetch<NomosEnvelope<{ configured: boolean }>>('GET', '/places/config');
  return { configured: !!r.data?.configured };
}

async function searchPlaces(query: string, sessionToken?: string, countries?: string): Promise<PlacesAutocompleteResponse> {
  const r = await nomosFetch<NomosEnvelope<PlacesAutocompleteResponse>>('GET', '/places/autocomplete', undefined,
    { query: { query, session_token: sessionToken, countries } });
  return { predictions: r.data?.predictions ?? [], configured: !!r.data?.configured };
}

async function getPlaceDetails(placeId: string, sessionToken?: string): Promise<PlaceDetailsResponse> {
  const url = `/places/${placeId}/details`;
  try {
    const r = await nomosFetch<NomosEnvelope<PlaceDetailsResponse & { location?: unknown }>>('GET', `/places/${encodeURIComponent(placeId)}/details`,
      undefined, { query: { session_token: sessionToken } });
    const { location: _location, ...sentinelShape } = r.data;
    return sentinelShape as PlaceDetailsResponse;
  } catch (e) {
    const err = e as { response?: { status?: number; data?: { error?: { code?: string } } } };
    const code = err.response?.data?.error?.code;
    if (code === 'places_not_configured') throw toAxiosError(503, { detail: 'Google Places API not configured' }, url);
    if (err.response?.status === 404) throw toAxiosError(404, { detail: 'Place not found' }, url);
    throw e;
  }
}

export const referenceDataImpl: NomosRegistry = {
  getEmissionFactorsBySource: getEmissionFactorsBySource as unknown as AnyFn,
  createEnergyPrice: createEnergyPrice as unknown as AnyFn,
  updateEnergyPrice: updateEnergyPrice as unknown as AnyFn,
  deleteEnergyPrice: deleteEnergyPrice as unknown as AnyFn,
  getClimateZones: getClimateZones as unknown as AnyFn,
  lookupClimateZone: lookupClimateZone as unknown as AnyFn,
  getEnergyBenchmarks: getEnergyBenchmarks as unknown as AnyFn,
  getBuildingTypeProfiles: getBuildingTypeProfiles as unknown as AnyFn,
  updateBuildingTypeProfile: updateBuildingTypeProfile as unknown as AnyFn,
  getGridEmissionFactors: getGridEmissionFactors as unknown as AnyFn,
  getClimateDataSummary: getClimateDataSummary as unknown as AnyFn,
  getBiddingZones: getBiddingZones as unknown as AnyFn,
  getSpotPrices: getSpotPrices as unknown as AnyFn,
  getCarbonIntensity: getCarbonIntensity as unknown as AnyFn,
  checkPlacesConfig: checkPlacesConfig as unknown as AnyFn,
  searchPlaces: searchPlaces as unknown as AnyFn,
  getPlaceDetails: getPlaceDetails as unknown as AnyFn,
};
